'use client'

import {useEffect, useMemo, useState} from 'react'
import {useSnapshot} from 'valtio'

import LinkCompaniesModal from '@/app/users/LinkCompaniesModal'
import UserRelationBadge from '@/app/users/UserRelationBadge'
import {closeNotif, startNotif} from '@/components/DynamicForm/util/notifier'
import Modal from '@/components/Modal/Modal'
import {Button} from '@/components/UiElements'
import Alert from '@/components/UiElements/Alert'
import unlinkUserFromCompany from '@/services/UserService/unlinkUserFromCompany'
import store from '@/store'
import {Companies} from '@/types/companies'
import {User} from '@/types/user'
import {getErrorMessage} from '@/utils/errorMessages'
import getUserCompanyIds from '@/utils/getUserCompanyIds'

type LinkUserToCompanyProps = {
    selectedUser: User
    onCompaniesChange?: (companies: Companies[]) => void
}

type RawCompany = string | {
    id?: string | null
    name?: string
    store_name?: string
}

const normalizeCompany = (
    company: RawCompany,
    availableCompanies: Companies[],
): Companies | null => {
    const companyId = typeof company === 'string' ? company : company.id
    if (!companyId) return null

    const matchedCompany = availableCompanies.find(item => (
        item.id === companyId || item.store_name === companyId
    ))
    const companyName = typeof company === 'string' ? undefined : company.name
    const storeName = typeof company === 'string' ? undefined : company.store_name

    return {
        id: matchedCompany?.id || companyId,
        name: companyName || matchedCompany?.name || storeName || matchedCompany?.store_name || companyId,
        store_name: storeName || matchedCompany?.store_name || '',
    }
}

const uniqueCompanies = (companies: Array<Companies | null>) => (
    Array.from(
        new Map(
            companies
                .filter((company): company is Companies => Boolean(company))
                .map(company => [company.id, company]),
        ).values(),
    )
)

const LinkUserToCompany = ({
    selectedUser,
    onCompaniesChange,
}: LinkUserToCompanyProps) => {
    const snap = useSnapshot(store)
    const [isLinkCompaniesModalOpen, setIsLinkCompaniesModalOpen] = useState(false)
    const [unlinkingCompanyId, setUnlinkingCompanyId] = useState<string | null>(null)

    const availableCompanies = useMemo(() => {
        const currentUser = snap.currentUser as unknown as {
            companyNames?: RawCompany[]
            companies?: RawCompany[]
        } | null
        const rawCompanies = currentUser?.companyNames?.length
            ? currentUser.companyNames
            : currentUser?.companies || []

        return uniqueCompanies(
            rawCompanies.map(company => normalizeCompany(company, [])),
        )
    }, [snap.currentUser])

    const initialLinkedCompanies = useMemo(() => {
        const rawCompanies: RawCompany[] = getUserCompanyIds(selectedUser)

        return uniqueCompanies(
            rawCompanies.map(company => normalizeCompany(company, availableCompanies)),
        )
    }, [availableCompanies, selectedUser])

    const [linkedCompanies, setLinkedCompanies] = useState<Companies[]>(initialLinkedCompanies)

    useEffect(() => {
        setLinkedCompanies(initialLinkedCompanies)
    }, [initialLinkedCompanies])

    const updateLinkedCompanies = (companies: Companies[]) => {
        setLinkedCompanies(companies)
        onCompaniesChange?.(companies)
    }

    const handleCompaniesLinked = (companies: Companies[]) => {
        updateLinkedCompanies(uniqueCompanies([...linkedCompanies, ...companies]))
    }

    const unlinkCompany = async (company: Companies) => {
        if (unlinkingCompanyId) return

        setUnlinkingCompanyId(company.id)
        const notificationId = startNotif('Unlinking company...')

        try {
            await unlinkUserFromCompany(selectedUser.email, company.id)
            updateLinkedCompanies(
                linkedCompanies.filter(linkedCompany => linkedCompany.id !== company.id),
            )
            closeNotif(notificationId, 'Company unlinked successfully.')
        } catch (error) {
            closeNotif(
                notificationId,
                getErrorMessage(error, 'Failed to unlink company.'),
                {type: 'error'},
            )
        } finally {
            setUnlinkingCompanyId(null)
        }
    }

    const handleUnlinkCompany = (company: Companies) => {
        Alert.showAlert({
            title: 'Unlink Company',
            message: (
                <div className="flex flex-col items-center justify-center">
                    Are you sure you want to unlink &quot;{company.name}&quot; from this user?
                </div>
            ),
            isCancelable: true,
            button: 'Unlink Company',
            danger: true,
            callBack: (confirmed) => {
                if (confirmed) void unlinkCompany(company)
            },
        })
    }

    return (
        <section className="border-t border-gray-200 pt-xl">
            <div className="mb-md">
                <p className="text-title-md font-medium text-gray-900">Companies</p>
            </div>

            <ul className="flex flex-wrap gap-sm">
                {linkedCompanies.map(company => (
                    <li key={company.id}>
                        <UserRelationBadge
                            title={company.name || company.store_name || company.id}
                            description={company.store_name && company.store_name !== company.name
                                ? company.store_name
                                : undefined}
                            onRemove={() => handleUnlinkCompany(company)}
                            disabled={Boolean(unlinkingCompanyId)}
                        />
                    </li>
                ))}
                <li>
                    <Button
                        size="sm"
                        type="alt"
                        icon="+"
                        className="!h-[64px] !w-[64px] !min-w-[64px] !p-0"
                        onClick={() => setIsLinkCompaniesModalOpen(true)}
                    />
                </li>
            </ul>

            {linkedCompanies.length === 0 && (
                <div className="mt-sm text-text-md text-gray-400">
                    No companies are linked to this user.
                </div>
            )}

            <Modal
                isOpen={isLinkCompaniesModalOpen}
                onClose={() => setIsLinkCompaniesModalOpen(false)}
                title="Link Companies"
                closeOnOutsideClick={false}
            >
                {isLinkCompaniesModalOpen && (
                    <LinkCompaniesModal
                        user={selectedUser}
                        companies={availableCompanies}
                        linkedCompanies={linkedCompanies}
                        onClose={() => setIsLinkCompaniesModalOpen(false)}
                        onCompaniesLinked={handleCompaniesLinked}
                    />
                )}
            </Modal>
        </section>
    )
}

export default LinkUserToCompany
